import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'

import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../constants/routes'
import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import MarkdownWrapper from '../utils/markdown-wrapper'
import ThreeFacts from './components/three-facts'
// import RelatedIssues from './components/related-issues'
import RelatedSlider from '../layout/related-slider'
import ResourceTile from '../resources/components/resource-tile'
import KeyIssueTile from './components/key-issue-tile'

import { createSelector } from '@reduxjs/toolkit'
import { Link } from 'react-router-dom'
import Breadcrumb from '../elements/breadcrumb'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/underline-02.svg'
import { ReactComponent as HeaderTrim } from '../../assets/svgs/key-issue-header-trim.svg'
import anime from 'animejs'
import { Helmet } from 'react-helmet'
import MetaracismQuote from '../content/bands/metaracism'

const selectActiveUrl = state => state.nav.activeUrl
const selectKeyIssueShorts = state => state.keyIssueShort
const selectKeyIssues = state => state.keyIssues
const selectResources = state => state.resources
const selectRelatedResourceIds = (state, keyIssue) => keyIssue?.relatedResourceIds || []

const makeRelatedResources = createSelector(
    [selectResources, selectRelatedResourceIds],
    (resources, relatedResourceIds) => {
        return resources.filter(resource => relatedResourceIds.includes(resource.uuid))
    }
)

const makeKeyIssue = createSelector(
    [selectActiveUrl, selectKeyIssueShorts],
    (activeUrl, keyIssueShorts) => {
        // remove /in-depth from the activeUrl
        let cleanUrl = activeUrl.replace('/in-depth', '')
        return keyIssueShorts.find(ki => ki.alias === cleanUrl)
    }
)

const makeRelatedIssues = createSelector(
    [makeKeyIssue, selectKeyIssues],
    (keyIssue, keyIssues) => {
        if (keyIssue?.keyIssueIds) {
            return keyIssues.filter(ki =>
                keyIssue.keyIssueIds.includes(ki.uuid)
            )
        }
        return []
    }
)

const mapStateToProps = (state, ownProps) => {
    const keyIssue = makeKeyIssue(state)
    const relatedIssues = makeRelatedIssues(state)
    const relatedResources = makeRelatedResources(state, keyIssue)
    return {
        keyIssue,
        relatedIssues,
        relatedResources,
        keyIssueCategories: state.keyIssueCategories,
        navState: state.nav
    }
}

const KeyIssueView = ({ dispatch, navState, keyIssue, keyIssueCategories, relatedIssues, relatedResources }) => {
    const componentName = 'Key Issue View'
    const activeUrl = navState.activeUrl.substring(1)
    const color = keyIssueCategories?.find(c => c.name === keyIssue?.category)?.color

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState()
        } else if (componentState === 'ANIMATING_IN') {
            // Turn the nav white if it isnt already and our theme is blue or orange
            if (navState.theme !== 'white' && ['blue', 'magenta', 'teal'].includes(color)) {
                dispatch(saveNavTheme('white'))
            } else {
                // Otherwise make sure it is grey
                dispatch(saveNavTheme('grey'))
            }
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (keyIssue) {
            dispatch(fetchApiData(apiResourceEndpoint(activeUrl, 'LIST')))
        }
    }, [activeUrl])

    const handlePreloadingState = async () => {
        // TODO: We may have the small version of the key issue already loaded but not the full one
        // if (!keyIssue) {
        dispatch(fetchApiData(apiResourceEndpoint(activeUrl, 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
        // } else {
        //     dispatch(completePreloading())
        // }
    }

    const handleAnimatingInState = async () => {
        const markerLine = animateLine()
        markerLine.finished.then(() => {
            dispatch(completeAnimatingIn())
        })
    }

    const handleAnimatingOutState = () => {
        dispatch(completeAnimatingOut())
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        return null
    }

    // TOOD: This is a resource issue as as the item may not have a slug on it, we should split back into short / long reducers
    if (!keyIssue || !keyIssue.slug) {
        return null
    }

    const renderRelatedKeyIssues = () => {
        if (relatedIssues.length === 0) {
            return null
        }
        let issues = relatedIssues.map((issue, index) => {
            return <KeyIssueTile key={index} keyIssue={issue} filterObjects={keyIssueCategories} />
        })
        return (
            <RelatedSlider title="Interconnected Issues" classes="related-key-issues">
                {issues}
            </RelatedSlider>
        )
    }

    const renderRelatedResources = () => {
        // Loop over and load related resources
        if (relatedResources.length === 0) {
            return null
        }
        let resources = relatedResources.map((resource, index) => {
            return <ResourceTile key={index} resource={resource} />
        })
        return (
            <RelatedSlider title="Related Resources" classes="related-resources">
                {resources}
            </RelatedSlider>
        )
    }

    const animateLine = () => {
        return anime({
            targets: '.title-stroke #marker',
            strokeDashoffset: 0,
            easing: 'easeInQuart',
            duration: 750
        })
    }

    const textBlock = keyIssue?.inDepthContent || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi nec odio in felis vehicula ultricies. Donec in purus nec erat ultricies ultrices. Nullam nec sapien vitae purus aliquam condimentum.'

    const { slug, pageTitle, category, metaDescription, primaryImage, title, contentBands, metaracismQuote } = keyIssue

    return (
        <>
            <Helmet>
                <title>A Deeper Look at {pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={primaryImage?.url} />
            </Helmet>
            <Breadcrumb>
                <Link to={routes.keyIssuesIndex.path}>Key Issues</Link>&nbsp;&bull;&nbsp;
                <Link to={buildRoutePath(routes.keyIssuesIndex.path, {}, { category })}>{category}</Link>&nbsp;&bull;&nbsp;
                <Link to={buildRoutePath(routes.keyIssueOverview.path, { keyIssueSlug: slug })}>{title}</Link>&nbsp;&bull;
                A Deeper Look
            </Breadcrumb>
            <div className="key-issue-view">
                <div className={`header ${color}-bg`}>
                    <div className="container">
                        <div className="header-content">
                            <div className="title-wrapper">
                                <div className="title-stroke">
                                    <h1>{keyIssue?.title}</h1>
                                    <MarkerStroke />
                                </div>
                            </div>
                            <div className="image">
                                <img src={primaryImage?.url} alt={primaryImage?.alt} />
                            </div>
                        </div>
                    </div>
                    <div className="header-trim">
                        <HeaderTrim />
                    </div>
                </div>
                <div className="content-bands">
                    <div className="container">
                        <div className="content-wrapper">
                            <div className="content-left">
                                <div className="text-block">
                                    <MarkdownWrapper content={textBlock} />
                                </div>
                            </div>
                            <div className="content-right">
                                {/* 3 Facts use map on keyIssueShort.contentBands then three facts components */}
                                {contentBands.map((contentBand, index) => (
                                    <ThreeFacts key={index} contentBand={contentBand} />
                                ))}
                            </div>
                        </div>
                    </div>
                    { metaracismQuote &&
                        <div className="container">
                            <MetaracismQuote content={metaracismQuote} />
                        </div>
                    }
                    <div className="container">
                        {/* Interconnected Issues */}
                        { renderRelatedKeyIssues() }
                    </div>
                    <div className="dark-bg">
                        <div className="container">
                            {/* Related Resources */}
                            { renderRelatedResources() }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default connect(mapStateToProps)(KeyIssueView)
